import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import validator from 'validator';

const EmailRepeater = ({
	name,
	value = [],
	onChange,
	label = 'Email',
	disabled = false,
	placeholder = 'Enter email address',
	addButtonText = 'Add Email',
	maxItems,
	...props
}) => {
	const [emails, setEmails] = useState(
		value && value.length > 0 ? value : [''],
	);

	useEffect(() => {
		if (value && value.length > 0) {
			// Limit to maxItems if set
			const limitedValue = maxItems !== undefined 
				? value.slice(0, maxItems)
				: value;
			setEmails(limitedValue);
		}
	}, [value, maxItems]);

	const isValidEmail = (email) => {
		if (!email || email.trim() === '') return true;
		return validator.isEmail(email);
	};

	const handleEmailChange = (index, newValue) => {
		const updatedEmails = [...emails];
		updatedEmails[index] = newValue;
		setEmails(updatedEmails);

		// Limit to maxItems if set when sending back to parent
		const valueToSend = maxItems !== undefined 
			? updatedEmails.slice(0, maxItems)
			: updatedEmails;
		onChange({ target: { name, value: valueToSend } });
	};

	const handleAddEmail = () => {
		// Prevent adding more emails if maxItems is set and limit is reached
		if (maxItems !== undefined && emails.length >= maxItems) {
			return;
		}
		
		const updatedEmails = [...emails, ''];
		setEmails(updatedEmails);
		
		onChange({ target: { name, value: updatedEmails } });
	};

	const handleRemoveEmail = (index) => {
		if (emails.length === 1) return;
		
		const updatedEmails = emails.filter((_, i) => i !== index);
		setEmails(updatedEmails);

		onChange({ target: { name, value: updatedEmails } });
	};

	// Limit displayed emails to maxItems if set
	const displayedEmails = maxItems !== undefined 
		? emails.slice(0, maxItems)
		: emails;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...props.sx }}>
			{displayedEmails.map((email, index) => (
				<TextField
					key={index}
					fullWidth
					label={index === 0 ? label : `${label} ${index + 1}`}
					value={email}
					onChange={(e) => handleEmailChange(index, e.target.value)}
					disabled={disabled}
					placeholder={placeholder}
					type="email"
					error={email.trim() !== '' && !isValidEmail(email)}
					helperText={
						email.trim() !== '' && !isValidEmail(email)
							? 'Please enter a valid email address'
							: ''
					}
					InputProps={{
						endAdornment: index > 0 ? (
							<InputAdornment position="end">
								<IconButton
									onClick={() => handleRemoveEmail(index)}
									disabled={disabled}
									edge="end"
									aria-label={`Remove email ${index + 1}`}
								>
									<CloseIcon />
								</IconButton>
							</InputAdornment>
						) : null,
					}}
				/>
			))}
			{/* Only show Add button if maxItems is not set or limit hasn't been reached */}
			{(maxItems === undefined || displayedEmails.length < maxItems) && (
				<Button
					variant="outlined"
					startIcon={<AddIcon />}
					onClick={handleAddEmail}
					disabled={disabled}
					sx={{
						alignSelf: 'flex-start',
						textTransform: 'none',
						borderRadius: 1,
					}}
				>
					{addButtonText}
				</Button>
			)}
		</Box>
	);
};

EmailRepeater.type = 'EmailRepeater';

EmailRepeater.spec = [{
	name: 'EmailRepeater',
	key: 'EmailRepeater',
	title: 'Email Repeater',
	icon: 'AtSign',
	category: 'input',
	translatable: ['label', 'placeholder', 'addButtonText'],
	fields: [{
		type: 'TextField',
		label: 'Label',
		name: 'label',
	}, {
		type: 'TextField',
		label: 'Placeholder',
		name: 'placeholder',
	}, {
		type: 'TextField',
		label: 'Add Button Text',
		name: 'addButtonText',
	}],
}];

export default EmailRepeater;

